// @ts-check
import NodeDataSource from 'components/Dashboard/datasources/NodeDataSource';
import ModelType from 'components/Model/ModelType';
import T from 'localization/LocalizationKeys';
import { AggregateModel, IntervalModel } from 'components/Model/Models';
import { Aggregation, TimeBucket } from 'services/constants';
import _ from 'lodash';
import { useMemo } from 'react';
import LastValueDataSource from 'components/Dashboard/datasources/LastValueDataSource';
import { useQuery } from '@tanstack/react-query';
import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';
import { useAssetsFromModelIds } from './util/SignalFunction';

/**
 * @typedef {object} SignalCoordinateValueType
 * @property {string} value
 */
/**
 * @param {SignalCoordinateValueType[]} value
 * @returns {{lat: number, long: number}|null}
 */
const parseLatLong = (value) => {
  if (value?.length === 1 && _.isString(value?.[0].value)) {
    const cleanValue = _.replace(value?.[0].value, /"/g, '');
    let latLongs = _.split(cleanValue, ' ');
    if (latLongs?.length === 1) {
      // Try another split
      latLongs = _.split(cleanValue, ',');
    }
    if (latLongs?.length === 2) {
      return { lat: parseFloat(latLongs[0]), long: parseFloat(latLongs[1]) };
    }
  } else if (value?.length === 2) {
    return { lat: parseFloat(value?.[0].value), long: parseFloat(value?.[1].value) };
  }
  return null;
};

const useLastValue = ({ nodes: nodesFromDatasource, signals }) => {
  const { nodes, isLoading: isLoadingNodes } = NodeDataSource({ nodes: nodesFromDatasource });
  const api = useAPI();
  const modelIds = useMemo(() => _.map(signals, 'modelId'), [signals]);
  const assets = useAssetsFromModelIds({ nodes, modelIds });
  const assetIds = useMemo(() => _.map(assets, 'id'), [assets]);

  const lastValueQuery = useQuery(['lastValue', assetIds],
    createQueryRequest(api.telemetry.telemetryForAssets),
    {
      refetchOnWindowFocus: false,
      enabled: !_.isEmpty(assetIds) && !isLoadingNodes
    });


  const coordinatesByAsset = useMemo(() => {
    const values = _.groupBy(_.flatMap(/** @type {object[]}*/(lastValueQuery.data), values => {
      return _.filter(values, ({ telemetryName }) => _.some(signals, { name: telemetryName }));
    }), 'entityId');
    return _.reduce(values, (result, value, key) => {
      const latLong = parseLatLong(value);
      if (latLong) {
        result[key] = latLong;
      }
      return result;
    }, {});
  }, [lastValueQuery.data, signals]);

  return useMemo(() => {
    return { coordinatesByAsset, isLoading: lastValueQuery.isLoading || isLoadingNodes, hasError: lastValueQuery.isError };
  }, [coordinatesByAsset, isLoadingNodes, lastValueQuery.isError, lastValueQuery.isLoading]);
};

const CoordinateSignalDataSource =
({
  nodes,
  coordinateInputType,
  coordinateSignal,
  latitudeSignals = [],
  longitudeSignals = [],
  signals = [],
  aggregate = Aggregation.MEAN,
  interval = TimeBucket.PT1H,
  timeRange,
  reloadTrigger,
  dashboardDate
}) => {

  const coordinateSignals = coordinateInputType === 'signal' ? coordinateSignal :
    [...(latitudeSignals ?? []), ...(longitudeSignals ?? [])];

  const { coordinatesByAsset, isLoading, hasError } = useLastValue({
    nodes, signals: coordinateSignals
  });

  const lastValue = LastValueDataSource({
    nodes, signals, aggregate, interval, timeRange, reloadTrigger, dashboardDate
  });

  const assetIds = useMemo(() => _.keys(coordinatesByAsset), [coordinatesByAsset]);

  // Combine the data from the last value and map it per asset and add coordinate
  const dataPerAsset = useMemo(() => _.reduce(assetIds, (result, assetId) => {
    result[assetId] = {
      coordinate: coordinatesByAsset[assetId],
      data: {
        data: _.filter(lastValue?.data?.data, { assetId }),
        signalSets: _.filter(lastValue?.data?.signalSets, { assetId })
      },
      signalData: _.filter(lastValue?.signalData, { assetId })
    };

    return result;
  }, {}),
  [assetIds, coordinatesByAsset, lastValue?.data?.data, lastValue?.data?.signalSets, lastValue?.signalData]);

  return useMemo(() => ({
    isLoading: isLoading || lastValue.isLoading,
    hasError: hasError || lastValue.hasError,
    coordinatesPerAsset: dataPerAsset
  }), [dataPerAsset, hasError, isLoading, lastValue.hasError, lastValue.isLoading]);

};

CoordinateSignalDataSource.models = (/** @type {object} */properties) => {
  return _.compact([
    ...NodeDataSource.models,
    {
      path: 'coordinateInputType',
      type: ModelType.RADIO_GROUP,
      label: T.model.coordinate.inputtype,
      radios: [
        { id: 'signal', label: T.common.onesignal },
        { id: 'signals', label: T.common.twosignals }
        // { id: 'properties', label: 'Properties' }
      ]
    },
    // TODO: Future use?
    /*
    properties.coordinateInputType === 'properties' && {
      path: 'coordinateProperty',
      type: ModelType.PROPERTIES,
      // TODO: Translation
      label: 'Coordinate Property'
    },

     */
    properties.coordinateInputType === 'signal' && {
      path: 'coordinateSignal',
      type: ModelType.SIGNALS,
      label: T.common.coordinates
    },
    properties.coordinateInputType === 'signals' && {
      path: 'latitudeSignals',
      type: ModelType.SIGNALS,
      label: T.common.latitude
    },
    properties.coordinateInputType === 'signals' && {
      path: 'longitudeSignals',
      type: ModelType.SIGNALS,
      label: T.common.longitude
    },
    {
      path: 'signals',
      type: ModelType.SIGNALS,
      label: T.common.signals
    },
    AggregateModel,
    IntervalModel,
    {
      path: 'timeRange',
      type: ModelType.TIME_RANGE,
      label: T.common.input.timerange.title
    }
  ]);
};

CoordinateSignalDataSource.getUsedSystemModels = (/** @type {object}*/data) => {
  if (data.coordinateInputType === 'signal' || data.coordinateInputType === 'signals') {
    const signals = _.compact([...data?.coordinateSignal ?? [], ...data?.latitudeSignals ?? [], ...data?.longitudeSignals ?? []]);
    const models = _.reduce(signals, (result, signal) => [...result, signal.modelId], []);
    return _.uniq(models);
  }
  return [];
};

CoordinateSignalDataSource.getUsedAssets = (/** @type {object} */data) => {
  return NodeDataSource.getUsedAssets(data);
};

export default CoordinateSignalDataSource;
